import { useEffect } from 'react';

/** Forces the touch tier on from a desktop browser, for design work and tests. */
const FORCE_PARAM = 'touch';

/**
 * Whether this device should get finger-sized controls.
 *
 * Pointer type, not viewport width: an iPad in landscape is 1024–1194px, the
 * same as a half-screen desktop window, so width cannot tell them apart.
 * iPadOS reports `coarse` even with a Magic Keyboard attached, which is what we
 * want — the screen is still the primary input.
 *
 * Exported for tests. jsdom implements no matchMedia at all, hence the guard.
 */
export function prefersTouchUi(): boolean {
  if (typeof window === 'undefined') return false;

  if (new URLSearchParams(window.location.search).has(FORCE_PARAM)) return true;

  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
}

/**
 * Puts `touch-ui` on <html> for the whole session.
 *
 * Every touch-tier rule in the app hangs off this one class, so it has to be
 * set above App's route dispatch — the login screen, the events list and the
 * replay view all need it, and each returns early.
 */
export function useTouchUi(): void {
  useEffect(() => {
    const root = document.documentElement;
    const touch = prefersTouchUi();
    root.classList.toggle('touch-ui', touch);

    // A pointer can appear or disappear mid-session: a Magic Keyboard being
    // attached, or the browser moving to another display.
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(pointer: coarse)');
    const onChange = () => root.classList.toggle('touch-ui', prefersTouchUi());

    // Safari only grew addEventListener on MediaQueryList in 14.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    if (typeof query.addListener === 'function') {
      query.addListener(onChange);
      return () => query.removeListener(onChange);
    }
  }, []);
}
